import {
    colorBaseGreyWarm600,
    headerTinyFontSize,
    IconArrowRight,
    layoutMarginSmall,
    ProductButton,
    ProductHeading,
} from '@issuu/silkscreen';
import type { types } from '@issuu/story-components';
import { BlocksContent, Story } from '@issuu/story-components';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { shouldShowAds } from '../../util/ads-utils';
import { AdsLeadStory } from '../Ads';
import DocumentContext from '../DocumentPage/DocumentContext';
import useTracking from './useTracking';

type HeaderBlock = types.Block$Header;

const Root = styled.aside`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    padding: 0 ${layoutMarginSmall};
    margin-bottom: ${layoutMarginSmall};
    grid-area: lead-story;
    * {
        max-width: 100%;
    }
`;

const Header = styled.div`
    text-transform: uppercase;
    margin: calc(${layoutMarginSmall} * 2) auto ${layoutMarginSmall};
    font-size: ${headerTinyFontSize};
    font-weight: bold;
    color: ${colorBaseGreyWarm600};
    letter-spacing: 1px;
    text-align: center;
`;

const WhiteMask = styled.div`
    position: absolute;
    bottom: 0;
    height: 6.5rem;
    width: 100%;
    background: linear-gradient(transparent, #fff);
`;

const StoryHeaderBlock = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 1em;
    overflow-wrap: break-word;
`;

const StoryHeader = ({ headline }: HeaderBlock) => (
    <StoryHeaderBlock>
        <ProductHeading level={2}>{headline?.text}</ProductHeading>
    </StoryHeaderBlock>
);

const READ_MORE_THRESHOLD = 4;

export const MobileLeadStory: React.FC = () => {
    const context = useContext(DocumentContext);
    const {
        leadArticleStoryContent,
        trackers: { story: storyTracker },
    } = context;
    if (!leadArticleStoryContent) return null;
    const { blocks, id } = leadArticleStoryContent;
    const rootRef = useTracking(id, storyTracker);
    const [readAll, setReadAll] = useState(blocks.length < READ_MORE_THRESHOLD);
    const headerElm = blocks[0].type === 'header' ? <StoryHeader {...blocks[0].header} /> : null;
    return (
        <Root aria-label="Lead story" ref={rootRef}>
            <Header>Lead story</Header>
            <Story>
                {headerElm}
                <BlocksContent
                    adElement={
                        shouldShowAds({ context }) ? <AdsLeadStory key="leadstory-ads" /> : null
                    }
                    blocks={readAll ? blocks : blocks.slice(0, READ_MORE_THRESHOLD)}
                />
                {!readAll ? <WhiteMask /> : null}
            </Story>
            {!readAll ? (
                <ProductButton
                    icon={<IconArrowRight role="presentation" />}
                    iconPlacement="right"
                    onClick={() => setReadAll(true)}
                    size="large"
                    variant="tertiary"
                >
                    Read full story
                </ProductButton>
            ) : null}
        </Root>
    );
};
